import React, { useState, useEffect } from 'react';
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig'; 

function Blog() {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState({}); 

  // Fetch blog posts from Cloud Function
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const getBlogPosts = httpsCallable(functions, 'getBlogPosts');
        const result = await getBlogPosts();
        const postsData = result.data.posts || [];

        // Validate and process posts
        const validPosts = postsData.map(post => {
          if (!post.id || !post.title || !post.text) {
            console.warn('Invalid post data (missing id, title, or text):', post);
            return null;
          }
          return {
            id: post.id,
            date: post.date, 
            blogSortOrder: parseInt(post.blogSortOrder, 10) || 0,
            image: post.image || '',
            title: post.title,
            text: post.text,
          };
        }).filter(post => post !== null);

        // Sort by blogSortOrder descending (higher numbers first, e.g., 4 is most recent)
        setPosts(validPosts.sort((a, b) => b.blogSortOrder - a.blogSortOrder));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err.message, err.details);
        setError('Failed to load blog posts. Please try again later or check your connection.');
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Fetch image URL when a post is selected or on initial load
  useEffect(() => {
    const loadImages = async () => {
      const loadedStates = {};
      for (const post of posts) {
        if (post.image) {
          try {
            const url = await getDownloadURL(ref(storage, post.image));
            loadedStates[post.id] = url; 
          } catch (err) {
            console.error(`Error loading image for post ${post.id}:`, err);
            loadedStates[post.id] = null; 
          }
        } else {
          loadedStates[post.id] = null; 
        }
      }
      setImageLoaded(loadedStates);
    };
    if (posts.length > 0) loadImages();
  }, [posts]);

  // Toggle post expansion
  const handlePostClick = (post) => {
    setSelectedPost(selectedPost?.id === post.id ? null : post);
  };

  // Placeholder image component with text
  const PlaceholderImage = ({ postId }) => (
    <div
      style={{
        width: '100%',
        height: '200px',
        backgroundColor: '#ccc',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: '14px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)', 
          zIndex: 1,
        }}
      />
      <span style={{ position: 'relative', zIndex: 2 }}>⏳ loading Image ⏳</span>
    </div>
  );

  // Render loading or error state
  if (loading) {
    return (
      <div
        className="body"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}
      >
        <p>⏳ Loading Blog Posts ⏳</p>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="body"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}
      >
        <p style={{ color: 'red' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 16px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {selectedPost ? (
        // Expanded view of a single post
        <div style={{ padding: '16px', flex: 1, overflowY: 'auto', marginTop: '120px', position: 'relative', zIndex: 0 }}>
          {imageLoaded[selectedPost.id] ? (
            <div style={{ position: 'relative', width: '100%', height: '200px', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
              <img
                src={imageLoaded[selectedPost.id]}
                alt={selectedPost.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.error(`Image load failed for post ${selectedPost.id}`);
                }}
              />
            </div>
          ) : (
            <PlaceholderImage postId={selectedPost.id} />
          )}
          <h2 style={{ padding: '5px', color: '#F48E28', marginBottom: '5px' }}>{selectedPost.title}</h2>
          <p style={{ padding: '1px', color: '#A68368' }}>{selectedPost.date}</p>
          <p style={{ whiteSpace: 'pre-wrap', padding: '5px', color: '#6C441A' }} dangerouslySetInnerHTML={{ __html: selectedPost.text || '' }} />
          <button
            onClick={() => handlePostClick(selectedPost)}
            style={{
              marginTop: '40px',
              padding: '8px 16px',
              cursor: 'pointer',
              borderWidth: 2,
              borderColor: '#F48E28',
              borderRadius: '8px',
              color: '#F48E28',
              fontWeight: '800',
              fontSize: 18,
              backgroundColor: 'rgba(255,255,255,0)',
            }}
          >
            Back to All Posts
          </button>
        </div>
      ) : (
        // List view of all posts
        <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
          <h1 style={{ paddingTop: '100px', marginTop: 0, color: '#A68368' }}>Sip & Reads</h1>
          <p style={{ padding: '10px', color: '#6C441A' }}>
            Stay updated with the latest coffee trends, cafe stories, and BeanBonus news. Check back for new posts!
          </p>

          {posts.length > 0 ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px',
              }}
            >
              {posts.map(post => (
                <div
                  key={post.id}
                  onClick={() => handlePostClick(post)}
                  style={{
                    cursor: 'pointer',
                    marginBottom: '0',
                    padding: '16px',
                    border: '3px solid #A68368',
                    borderRadius: '15px',
                    backgroundColor: '#f9f9f9',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.border = '5px solid #F48E28')}
                  onMouseLeave={e => (e.currentTarget.style.border = '3px solid #A68368')}
                >
                  <div style={{ position: 'relative', width: '100%', height: '200px', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
                    {imageLoaded[post.id] ? (
                      <>
                        <img
                          src={imageLoaded[post.id]}
                          alt={post.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            console.error(`Image load failed for post ${post.id}`);
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '0',
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.4)', 
                            zIndex: 1,
                          }}
                        />
                        <h2
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            zIndex: 2,
                            textAlign: 'center',
                            padding: '5px',
                            margin: 0,
                            fontSize: '1.2em',
                            textShadow: '1px 1px 3px rgba(0, 0, 0, 0.7)',
                          }}
                        >
                          {post.title}
                        </h2>
                      </>
                    ) : (
                      <PlaceholderImage postId={post.id} />
                    )}
                  </div>
                  <p style={{ padding: '1px', color: '#A68368' }}>{post.date}</p>
                  <p style={{ padding: '5px', color: '#6C441A' }}>
                    {((post.text.replace(/<br\s*\/>/gi, ' ') || '').substring(0, 120) || 'No preview available') + '...'}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p>No posts available yet.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Blog;